import React, { useState } from 'react';
import { _T, C } from '../../shared/theme';
import { s_PB, s_GB, s_GC, s_PL } from '../../shared/styles';
import { loadGameHistory, weekBoundaries, avgScoreInWindow, lastNScores } from '../../shared/utils';
import Sparkline from '../../components/ui/Sparkline';

function GameTrainingTab({lang}) {
  const nl = lang==="nl";
  const CATEGORIES = [
    {id:"focus",     label:nl?"Concentratie":"Focus",       icon:"🎯", color:"#D4873A", games:["stroop","focustap","oddsymbol"]},
    {id:"memory",    label:nl?"Geheugen":"Memory",          icon:"🧠", color:"#7B6FD0", games:["memory","sequence","wordmemory"]},
    {id:"reaction",  label:nl?"Reactietijd":"Reaction",     icon:"⚡", color:"#C8A84B", games:["reaction","directionflash","peripheralflash"]},
    {id:"cognition", label:nl?"Cognitie":"Cognition",       icon:"➕", color:"#5B9ED0", games:["math","numchain"]},
    {id:"social",    label:nl?"Sociaal":"Social",           icon:"🪞", color:"#7B6FD0", games:["tonedecoder","empathymirror"]},
  ];
  const [view, setView] = useState("month"); // month | week
  const [selCat, setSelCat] = useState("all");

  // Build monthly data for the past 12 months
  const now = new Date();
  const months = Array.from({length:12}, (_,i) => {
    const d = new Date(now.getFullYear(), now.getMonth()-11+i, 1);
    return {
      label: d.toLocaleDateString(nl?"nl-NL":"en-US",{month:"short"}),
      year: d.getFullYear(),
      month: d.getMonth(),
      start: d.getTime(),
      end: new Date(d.getFullYear(), d.getMonth()+1, 1).getTime(),
    };
  });

  // Build weekly data for past 12 weeks
  const weeks = Array.from({length:12}, (_,i) => {
    const end = Date.now() - (11-i)*7*24*3600*1000;
    const start = end - 7*24*3600*1000;
    const d = new Date(start);
    return {
      label: `W${d.getDate()}/${d.getMonth()+1}`,
      start, end,
    };
  });

  const periods = view==="month" ? months : weeks;

  // For each category, compute avg normalised score per period (0-100)
  const getCatScore = (cat, start, end) => {
    const scores = [];
    cat.games.forEach(gid => {
      const hist = loadGameHistory(gid);
      const entries = hist.filter(h=>h.ts>=start&&h.ts<end);
      if (!entries.length) return;
      const avg = entries.reduce((a,h)=>a+h.score,0)/entries.length;
      // Normalise to 0-100
      if (gid==="memory")          scores.push(Math.min(100, avg));           // stored as 100-moves, ~100 max
      if (gid==="reaction")        scores.push(Math.min(100, avg/8));         // stored as 1000-ms, ~800 max → /8
      if (gid==="math")            scores.push(Math.min(100, avg/0.4));       // 40 pts = 100
      if (gid==="sequence")        scores.push(Math.min(100, avg/0.15));      // 15 levels = 100
      if (gid==="stroop")          scores.push(Math.min(100, avg/0.2));       // 20 correct = 100
      if (gid==="focustap")        scores.push(Math.min(100, avg/0.4));       // ~40 pts max = 100
      if (gid==="oddsymbol")       scores.push(Math.min(100, avg));           // 0-100 pts
      if (gid==="directionflash")  scores.push(Math.min(100, avg/14));        // stored as 1500-ms, ~1400 max → /14
      if (gid==="peripheralflash") scores.push(Math.min(100, avg/14));        // same as directionflash
      if (gid==="numchain")        scores.push(Math.min(100, avg/14));        // stored as 1500-avgMs/6, ~1400 max → /14
      if (gid==="wordmemory")      scores.push(Math.min(100, (avg/20)*100));  // 0-20 pts
      if (gid==="tonedecoder")    scores.push(Math.min(100, (avg/10)*100));  // 0-10 correct
      if (gid==="empathymirror")  scores.push(Math.min(100, (avg/8)*100));   // 0-8 correct
    });
    if (!scores.length) return null;
    return Math.round(scores.reduce((a,b)=>a+b,0)/scores.length);
  };

  const cats = selCat==="all" ? CATEGORIES : CATEGORIES.filter(c=>c.id===selCat);

  // Build dataset per category
  const datasets = cats.map(cat => ({
    ...cat,
    data: periods.map(p => getCatScore(cat, p.start, p.end)),
  }));

  const hasAnyData = datasets.some(d=>d.data.some(v=>v!==null));

  // SVG line chart
  const chartW = 300, chartH = 120, padL=28, padB=20, padT=8, padR=8;
  const innerW = chartW-padL-padR;
  const innerH = chartH-padB-padT;
  const xPos = (i) => padL + (i/(periods.length-1))*innerW;
  const yPos = (v) => padT + innerH - (v/100)*innerH;

  const makePath = (data) => {
    const pts = data.map((v,i)=>v!==null?{x:xPos(i),y:yPos(v)}:null).filter(Boolean);
    if (pts.length < 2) return null;
    return pts.map((p,i)=>`${i===0?"M":"L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  };

  // Per-category stats
  const catStats = CATEGORIES.map(cat => {
    const thisW = weekBoundaries(0);
    const lastW = weekBoundaries(1);
    const scoreNow = getCatScore(cat, thisW.start, thisW.end);
    const scoreLast = getCatScore(cat, lastW.start, lastW.end);
    const allHist = cat.games.flatMap(gid=>loadGameHistory(gid));
    return {...cat, scoreNow, scoreLast, sessions: allHist.filter(h=>h.ts>=thisW.start).length, total: allHist.length};
  });

  return (
    <div className="fadeUp">
      {/* Category filter */}
      <div style={{display:"flex",gap:5,flexWrap:"nowrap",overflowX:"auto",marginBottom:12,paddingBottom:4}}>
        <button style={{...s_PL(selCat==="all"),flexShrink:0}} onClick={()=>setSelCat("all")}>{nl?"Alles":"All"}</button>
        {CATEGORIES.map(c=><button key={c.id} style={{...s_PL(selCat===c.id,c.color),flexShrink:0}} onClick={()=>setSelCat(c.id)}>{c.icon} {c.label}</button>)}
      </div>

      {/* Period toggle */}
      <div style={{display:"flex",background:_T.bg4,borderRadius:12,padding:3,marginBottom:14,gap:2}}>
        {[{id:"month",label:nl?"Maand":"Month"},{id:"week",label:nl?"Week":"Week"}].map(v=>(
          <button key={v.id} onClick={()=>setView(v.id)} style={{flex:1,background:view===v.id?`linear-gradient(135deg,${_T.grad1},${_T.grad2})`:"transparent",border:"none",borderRadius:9,padding:"8px",cursor:"pointer",fontSize:12,color:view===v.id?"#0a1220":C.silver,fontWeight:view===v.id?700:400}}>
            {v.label}
          </button>
        ))}
      </div>

      {/* Line chart */}
      <div style={s_GC({padding:"14px 10px 10px",marginBottom:14,overflowX:"auto"})}>
        <div style={{fontSize:13,color:_T.accent,letterSpacing:2,fontWeight:600,marginBottom:10,paddingLeft:4}}>
          {view==="month"?(nl?"AFGELOPEN 12 MAANDEN":"LAST 12 MONTHS"):(nl?"AFGELOPEN 12 WEKEN":"LAST 12 WEEKS")}
        </div>
        {!hasAnyData ? (
          <div style={{textAlign:"center",padding:"20px 0",color:C.silver,fontSize:12}}>
            {nl?"Speel games om je trainingsdata te zien 🎮":"Play games to see your training data 🎮"}
          </div>
        ) : (
          <svg width="100%" viewBox={`0 0 ${chartW} ${chartH}`} style={{overflow:"visible"}}>
            <defs>
              {datasets.map(d=>(
                <linearGradient key={d.id} id={`tg${d.id}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={d.color} stopOpacity="0.25"/>
                  <stop offset="100%" stopColor={d.color} stopOpacity="0"/>
                </linearGradient>
              ))}
            </defs>
            {/* Grid lines */}
            {[0,25,50,75,100].map(v=>(
              <g key={v}>
                <line x1={padL} y1={yPos(v)} x2={chartW-padR} y2={yPos(v)} stroke={`${C.silver}18`} strokeWidth="1"/>
                <text x={padL-4} y={yPos(v)+3} textAnchor="end" fill={C.silver} fontSize="7" fontFamily="Inter">{v}</text>
              </g>
            ))}
            {/* X labels — show every 2nd */}
            {periods.map((p,i)=>i%2===0&&(
              <text key={i} x={xPos(i)} y={chartH-4} textAnchor="middle" fill={C.silver} fontSize="7" fontFamily="Inter">{p.label}</text>
            ))}
            {/* Area fills */}
            {datasets.map(d=>{
              const pts = d.data.map((v,i)=>v!==null?{x:xPos(i),y:yPos(v)}:null).filter(Boolean);
              if (pts.length < 2) return null;
              const areaD = pts.map((p,i)=>`${i===0?"M":"L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ")
                + ` L${pts[pts.length-1].x},${padT+innerH} L${pts[0].x},${padT+innerH} Z`;
              return <path key={d.id+"area"} d={areaD} fill={`url(#tg${d.id})`}/>;
            })}
            {/* Lines */}
            {datasets.map(d=>{
              const path = makePath(d.data);
              if (!path) return null;
              return <path key={d.id} d={path} fill="none" stroke={d.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>;
            })}
            {/* Dots for last point */}
            {datasets.map(d=>{
              const lastIdx = d.data.map((v,i)=>v!==null?i:-1).filter(i=>i>=0).pop();
              if (lastIdx===undefined) return null;
              return <circle key={d.id+"dot"} cx={xPos(lastIdx)} cy={yPos(d.data[lastIdx])} r="3.5" fill={d.color}/>;
            })}
          </svg>
        )}
        {/* Legend */}
        {hasAnyData && (
          <div style={{display:"flex",gap:10,flexWrap:"wrap",marginTop:6,paddingLeft:4}}>
            {datasets.map(d=>(
              <div key={d.id} style={{display:"flex",alignItems:"center",gap:4}}>
                <div style={{width:12,height:3,borderRadius:2,background:d.color}}/>
                <span style={{fontSize:9,color:C.silver}}>{d.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Per-category stat cards */}
      <div style={{fontSize:13,color:_T.accent,letterSpacing:2,fontWeight:600,marginBottom:10}}>{nl?"CATEGORIEËN DEZE WEEK":"CATEGORIES THIS WEEK"}</div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14}}>
        {catStats.map(c=>{
          const delta = c.scoreNow!==null&&c.scoreLast!==null ? c.scoreNow-c.scoreLast : null;
          return (
            <div key={c.id} style={{background:_T.bg4,borderRadius:14,padding:"12px 14px",border:`1px solid ${c.color}22`,opacity:c.total>0?1:0.5}}>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8}}>
                <span style={{fontSize:18}}>{c.icon}</span>
                <span style={{fontSize:11,color:c.color,fontWeight:600}}>{c.label}</span>
                {c.sessions>0&&<span style={{marginLeft:"auto",fontSize:9,color:C.silver,background:`${c.color}15`,padding:"1px 6px",borderRadius:8}}>{c.sessions}x</span>}
              </div>
              <div style={{height:4,background:_T.bg3,borderRadius:2,overflow:"hidden",marginBottom:8}}>
                <div style={{width:`${c.scoreNow??0}%`,height:"100%",background:`linear-gradient(90deg,${c.color}66,${c.color})`,borderRadius:2,transition:"width 0.5s"}}/>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:18,color:C.white,fontWeight:700}}>{c.scoreNow!==null?`${c.scoreNow}`:"-"}<span style={{fontSize:13,color:C.silver}}>/100</span></span>
                {delta!==null
                  ? <span style={{fontSize:11,color:delta>=0?C.teal:C.orange,fontWeight:600}}>{delta>=0?"+":""}{delta}</span>
                  : <span style={{fontSize:13,color:C.silver}}>{c.total>0?(nl?"1 wk data":"1 wk data"):(nl?"Geen data":"No data")}</span>
                }
              </div>
            </div>
          );
        })}
      </div>

      {/* Total sessions summary */}
      <div style={s_GC({padding:"12px 16px"})}>
        <div style={{fontSize:13,color:_T.accent,letterSpacing:2,fontWeight:600,marginBottom:10}}>{nl?"TOTAAL OVERZICHT":"TOTAL OVERVIEW"}</div>
        {catStats.map((c,i)=>(
          <div key={c.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:i<catStats.length-1?`1px solid ${_T.cardBorderBot}`:"none"}}>
            <span style={{fontSize:16}}>{c.icon}</span>
            <div style={{flex:1}}>
              <div style={{color:C.white,fontSize:12,fontWeight:500}}>{c.label}</div>
              <div style={{color:C.silver,fontSize:13}}>{c.total} {nl?"sessies totaal":"sessions total"}</div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{color:c.color,fontSize:13,fontWeight:700}}>{c.scoreNow!==null?`${c.scoreNow}/100`:"–"}</div>
              <div style={{fontSize:9,color:C.silver}}>{nl?"deze week":"this week"}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default GameTrainingTab;
